import { getIsAnnualExpansionNotificationAvailable } from 'apps/dashboard/helpers/annualExpansion/annualExpansionSelectors'
import { isWaServiceMessagesChargeBannerVisible } from 'apps/whatsApp/helpers/waServiceMessagesChargeGate'
import { CoexistenceContactImportStatus } from 'apps/whatsApp/interfaces/whatsAppCoexistence'
import { getWhatsAppCoexistenceContactImportStatus } from 'apps/whatsApp/redux/coexistenceSlice'
import { getTotal } from 'common/audience/selectors/audienceSelectors'
import { getMigrationData } from 'common/billing'
import { SubscriptionAction } from 'common/billing/constants/SubscriptionAction'
import { TouchedContactLimits } from 'common/billing/constants/TouchedContactLimits'
import {
  getIsV3,
  MigrationType,
  ProMigrationType,
} from 'common/billing/entities/billingVersions/selectors'
import {
  getScheduledSubscriptionChangeData,
  getSubscriptionAction,
} from 'common/billing/entities/subscription/selectors'
import { LOW_BALANCE } from 'common/billing/wallet/walletConstants'
import {
  getAutoRefillStatus,
  getBalance,
  getIsCreditWallet,
  hasWallet,
} from 'common/billing/wallet/walletSelectors'
import {
  hasWhatsAppProErrorStatus,
  isWaCoexistenceContactImportCommunicationAvailable,
} from 'common/builder/selectors/builder/whatsAppSelectors'
import {
  NotificationRemoveType,
  StaticNotification,
  StaticNotificationId,
} from 'common/core/components/NotifyBar/NotifyInterfaces'
import { AnnualExpansion } from 'common/core/components/NotifyBar/StaticNotifications/components/AnnualExpansionNotification'
import { TikTokUsRegionRestrictedNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/TikTokUsRegionRestrictedNotification'
import { WaCoexistenceContactImportActiveNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/WaCoexistenceContactImportActiveNotification'
import { WaCoexistenceContactImportAvailableNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/WaCoexistenceContactImportAvailableNotification'
import { WaCoexistenceContactImportFinishedNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/WaCoexistenceContactImportFinishedNotification'
import { WaCreditLineIssueNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/WaCreditLineIssueNotification'
import { WaPricingUpdateNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/WaPricingUpdateNotification'
import { WaServiceMessagesChargeNotification } from 'common/core/components/NotifyBar/StaticNotifications/components/WaServiceMessagesChargeNotification'
import { Ability } from 'common/core/constants/Ability'
import { NotificationBarType } from 'common/core/constants/NotificationBarType'
import {
  getFacebookBannerAction,
  getInstagramBannerAction,
} from 'common/core/lib/refreshPermissions'
import { isAnyPro } from 'common/core/models/Account/helpers'
import { getAbility } from 'common/core/selectors/abilitiesSelectors'
import {
  getCurrentAccount,
  getIsAccountFlagEnabled,
  getIsAfterExpiredPeriod,
  isAccountBlocked,
} from 'common/core/selectors/appSelectors'
import {
  getSMSChannel,
  getTikTokChannelUsRegionRestricted,
  getWhatsAppChannel,
  hasConnectedEmailOrWhatsAppChannel,
  hasConnectedInstagramChannel,
  isWhatsAppChannelConnected,
  whatsAppChannelHasCreditLineIssue,
} from 'common/core/selectors/channelSelectors'
import { getCurrentAccountIsDummyMode } from 'common/core/selectors/currentAccountSelectors'
import { getRefreshPermissionsData } from 'common/core/selectors/refreshPermissionsSelectors'
import { getIsIgChangeAccountError } from 'common/core/selectors/uiSelectors'
import { getShouldShowIconBlockedNotification } from 'common/fusion/selectors'
import { getCardCountry, getIsNeedToShowPayNow } from 'common/settings/selectors/settingsSelectors'
import { FlexToV3MigrationState } from 'shared/api/requests/billing/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

import {
  ActivateProTrialNotification,
  AfterExpiredPeriodNotification,
  BlockedAccountNotification,
  BlockedByValueMetricNotification,
  BlockedSMSChannel,
  ExpiredAccountNotification,
  ExploreModeConnectInstagramNotification,
  ForcedNpmMigrationNotification,
  IconBlockedNotification,
  IgChangeAccountTypeErrorNotification,
  InstagramPageConnectionLost,
  PaymentCardRenewalNotification,
  PayNowNotification,
  PlanDowngradeNotification,
  RefreshAdminPermissionsNotification,
  VerifySubscriptionPaymentNotification,
  WAFreePeriodEndNotification,
  WalletLowBalanceNotification,
  WhatsAppBlockedNotification,
} from './components'
import { Discount90DaysNotification } from './components/Discount90DaysNotification'
import { MigrateFreeUsersToNewBillingNotification } from './components/MigrateFreeUsersToNewBillingNotification'
import { PostMigrationFreeUsersToNewBillingNotification } from './components/PostMigrationFreeUsersToNewBillingNotification'

const getIsSubscriptionActionEqual = (action: SubscriptionAction) => (state: RootState) =>
  getSubscriptionAction(state) === action

export const BlockedSMSChannelNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.BLOCKED_SMS_ACCOUNT,
  render: BlockedSMSChannel,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  ts_created: 1,
  shouldBeShown: (state: RootState) => getSMSChannel(state).is_blocked,
}

export const AfterExpiredPeriodNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.AFTER_EXPIRED_PERIOD,
  render: AfterExpiredPeriodNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) =>
    getIsAfterExpiredPeriod(state) &&
    !getIsSubscriptionActionEqual(SubscriptionAction.CONTINUE_CHECKOUT)(state),
}

export const ExpiredNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.EXPIRED,
  render: ExpiredAccountNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    return (
      !getIsNeedToShowPayNow(state) &&
      !getIsAfterExpiredPeriod(state) &&
      getIsSubscriptionActionEqual(SubscriptionAction.RENEW)(state)
    )
  },
}

export const BlockedAccountNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.BLOCKED_ACCOUNT,
  render: BlockedAccountNotification,
  type: NotificationBarType.ERROR,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) =>
    isAccountBlocked(state) &&
    !getIsSubscriptionActionEqual(SubscriptionAction.CONTINUE_CHECKOUT)(state),
}

export const WAFreePeriodEndNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_FREE_PERIOD_END,
  render: WAFreePeriodEndNotification,
  type: NotificationBarType.ERROR,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const waChannel = getWhatsAppChannel(state)
    if (!isWhatsAppChannelConnected(waChannel)) return false
    return !hasWhatsAppProErrorStatus(state) && Boolean(waChannel?.is_exceeded_free_limits)
  },
}

export const RefreshAdminPermissionsNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.REFRESH_ADMIN_PERMISSIONS,
  render: RefreshAdminPermissionsNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => Boolean(getFacebookBannerAction(getRefreshPermissionsData(state))),
}

export const IgRefreshPermissionsNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.INSTAGRAM_PAGE_CONNECTION_LOST,
  render: InstagramPageConnectionLost,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => Boolean(getInstagramBannerAction(getRefreshPermissionsData(state))),
}

// For Indian payment system only
export const PaymentCardRenewalNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.PAYMENT_CARD_RENEWAL,
  render: PaymentCardRenewalNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const account = getCurrentAccount(state)
    const enabledIndian3dsFlag = getIsAccountFlagEnabled(
      state,
      AccountFlag.ENABLE_INDIAN_CUSTOMERS_3DS_RESUBSCRIPTION,
    )
    return (
      getCardCountry(state)?.toUpperCase() === 'IN' && enabledIndian3dsFlag && !account.isExpired
    )
  },
}

export const IGChangeAccountTypeErrorNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.IG_CHANGE_ACCOUNT_TYPE_ERROR,
  render: IgChangeAccountTypeErrorNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const isShowIgChangeAccountError = getIsIgChangeAccountError(state)
    return isShowIgChangeAccountError
  },
}

export const PayNowNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.PAY_NOW,
  render: PayNowNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: getIsNeedToShowPayNow,
}

export const WalletLowBalanceNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.LOW_BALANCE,
  render: WalletLowBalanceNotification,
  type: NotificationBarType.INFO,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const balance = getBalance(state)
    const walletStatus = hasWallet(state)
    const autoRefillStatus = getAutoRefillStatus(state)
    const hasPaidChannel = hasConnectedEmailOrWhatsAppChannel(state)
    const isCreditWallet = getIsCreditWallet(state)
    const subscribersCount = getTotal(state)

    if (!walletStatus || !hasPaidChannel || isCreditWallet || subscribersCount < 5) return false

    return balance < LOW_BALANCE && !autoRefillStatus
  },
}

export const WhatsAppBlockedNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_PRO_ERROR,
  render: WhatsAppBlockedNotification,
  type: NotificationBarType.ERROR,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) =>
    hasWhatsAppProErrorStatus(state) &&
    !getIsSubscriptionActionEqual(SubscriptionAction.CONTINUE_CHECKOUT)(state),
}

export const ActivateProTrialNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.ACTIVATE_PRO_TRIAL,
  render: ActivateProTrialNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  isAlwaysOnTop: true,
  shouldBeShown: (state) => {
    if (getCurrentAccountIsDummyMode(state)) return false
    const isMigrateFreeUsersToNewBillingShown =
      MigrateFreeUsersToNewBillingNotificationConfig.shouldBeShown(state)
    const isDiscount90DaysShown = Discount90DaysNotificationConfig.shouldBeShown(state)
    const isPostMigrationFreeUsersToNewBillingShown =
      PostMigrationFreeUsersToNewBillingNotificationConfig.shouldBeShown(state)
    return (
      !isDiscount90DaysShown &&
      !isMigrateFreeUsersToNewBillingShown &&
      !isPostMigrationFreeUsersToNewBillingShown &&
      getIsSubscriptionActionEqual(SubscriptionAction.ACTIVATE_TRIAL)(state)
    )
  },
}

export const VerifySubscriptionPaymentNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.VERIFY_SUBSCRIPTION_PAYMENT,
  render: VerifySubscriptionPaymentNotification,
  type: NotificationBarType.ERROR,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  isAlwaysOnTop: true,
  shouldBeShown: getIsSubscriptionActionEqual(SubscriptionAction.CONTINUE_CHECKOUT),
}

export const WaHistoryContactImportStartedNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_COEXISTENCE_CONTACT_IMPORT_ACTIVE,
  render: WaCoexistenceContactImportActiveNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  ts_created: 1,
  shouldBeShown: (state) =>
    getWhatsAppCoexistenceContactImportStatus(state) === CoexistenceContactImportStatus.ACTIVE,
}

export const WaHistoryContactImportFinishedNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_COEXISTENCE_CONTACT_IMPORT_FINISHED,
  render: WaCoexistenceContactImportFinishedNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  ts_created: 1,
  shouldBeShown: (state) =>
    getWhatsAppCoexistenceContactImportStatus(state) === CoexistenceContactImportStatus.FINISHED,
}

export const WaCreditLineIssueNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_CREDIT_LINE_ISSUE,
  render: WaCreditLineIssueNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const account = getCurrentAccount(state)
    return whatsAppChannelHasCreditLineIssue(state) && isAnyPro(account)
  },
}

export const TikTokChannelUsRegionRestrictionNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.TIKTOK_US_REGION_RESTRICTED,
  render: TikTokUsRegionRestrictedNotification,
  type: NotificationBarType.ERROR,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => getTikTokChannelUsRegionRestricted(state),
}

export const WaCoexistenceContactImportAvailableNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_COEXISTENCE_CONTACT_IMPORT_AVAILABLE,
  render: WaCoexistenceContactImportAvailableNotification,
  type: NotificationBarType.INFO,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const shouldShowNotification = isWaCoexistenceContactImportCommunicationAvailable(state)
    return shouldShowNotification
  },
}

export const WaPricingUpdateNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_PRICING_UPDATE,
  render: WaPricingUpdateNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    // It should be visible from June 18th 2026 to July 7th 2026, with the pricing update going live on April 1st 2026.
    const shouldBeVisible =
      Date.now() >= Date.UTC(2026, 5, 18) && Date.now() <= Date.UTC(2026, 6, 7)

    return isWhatsAppChannelConnected(getWhatsAppChannel(state)) && shouldBeVisible
  },
}

export const WaServiceMessagesChargeNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.WA_SERVICE_MESSAGES_CHARGE,
  render: WaServiceMessagesChargeNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) =>
    isWhatsAppChannelConnected(getWhatsAppChannel(state)) &&
    isWaServiceMessagesChargeBannerVisible(),
}

export const PlanDowngradeNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.PLAN_DOWNGRADE,
  render: PlanDowngradeNotification,
  type: NotificationBarType.WARNING,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: (state) => {
    const scheduledChange = getScheduledSubscriptionChangeData(state)
    return scheduledChange?.type === 'downgrade'
  },
}

export const BlockedByValueMetricNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.BLOCKED_BY_VALUE_METRIC,
  render: BlockedByValueMetricNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  shouldBeShown: (state) => {
    const activeSubscribersAbility = getAbility(state, Ability.ACTIVE_SUBSCRIBERS)
    const isV3 = getIsV3(state)
    return activeSubscribersAbility.isLimited && isV3
  },
}

export const MigrateFreeUsersToNewBillingNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.MIGRATE_FREE_USERS_TO_NEW_BILLING,
  render: MigrateFreeUsersToNewBillingNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  shouldBeShown: (state) => {
    const migrationData = getMigrationData(state)
    const isV3 = getIsV3(state)
    return migrationData?.type === MigrationType.FREE && !isV3
  },
}

export const Discount90DaysNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.DISCOUNT_90_DAYS,
  render: Discount90DaysNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  shouldBeShown: (state) => {
    const isV3 = getIsV3(state)
    const account = getCurrentAccount(state)
    const isEligibleAccount = account.isFree || account.isExpired
    const migrationData = getMigrationData(state)
    const promoteEssential =
      migrationData && migrationData.type === MigrationType.FREE
        ? migrationData.data.activeContacts <= TouchedContactLimits.essential
        : false
    const isDiscount90DaysBannerEnabled = getIsAccountFlagEnabled(
      state,
      AccountFlag.ENABLE_DISCOUNT_90DAYS_BANNER,
    )
    return isV3 && isEligibleAccount && isDiscount90DaysBannerEnabled && promoteEssential
  },
}

export const PostMigrationFreeUsersToNewBillingNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.POST_MIGRATION_FREE_USERS_TO_NEW_BILLING,
  render: PostMigrationFreeUsersToNewBillingNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  shouldBeShown: (state) => {
    const currentAccount = getCurrentAccount(state)
    const hasMigrated = Boolean(currentAccount.has_migrated_free_user_npm)
    const isV3 = getIsV3(state)
    const isEligibleAccount = currentAccount.isFree || currentAccount.isExpired
    return hasMigrated && isV3 && isEligibleAccount
  },
}

export const ExploreModeConnectInstagramNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.EXPLORE_MODE_CONNECT_INSTAGRAM,
  render: ExploreModeConnectInstagramNotification,
  type: NotificationBarType.DEFAULT,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'InstagramChannel',
  shouldBeShown: getCurrentAccountIsDummyMode,
}

export const ForcedNpmMigrationNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.FORCED_NPM_MIGRATION,
  render: ForcedNpmMigrationNotification,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  shouldBeShown: (state) => {
    const migrationData = getMigrationData(state)
    if (!migrationData || migrationData.type !== MigrationType.PRO) {
      return false
    }
    if (
      migrationData.data.state !== FlexToV3MigrationState.SCHEDULED ||
      migrationData.data.isCancelling
    ) {
      return false
    }

    // The banner is shown for 30 days after the migration activates, which is when it's due
    const { dueDate } = migrationData.data
    if (dueDate && Date.now() >= dueDate.getTime()) {
      return false
    }
    return (
      migrationData.data.migrationType === ProMigrationType.INVOLUNTARY ||
      migrationData.data.migrationType === ProMigrationType.VOLUNTARY
    )
  },
}

export const AnnualExpansionNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.ANNUAL_EXPANSION,
  render: AnnualExpansion,
  type: NotificationBarType.SUCCESS,
  removeType: NotificationRemoveType.TEMPORARY,
  isWithoutReadMore: true,
  ts_created: 1,
  customIconName: 'OrphanBuyButton',
  shouldBeShown: (state) => {
    const isV3 = getIsV3(state)
    const isInstagramChannelConnected = hasConnectedInstagramChannel(state)

    return isV3 && !isInstagramChannelConnected && getIsAnnualExpansionNotificationAvailable(state)
  },
}

export const IconBlockedNotificationConfig: StaticNotification = {
  notification_id: StaticNotificationId.ICON_BLOCKED,
  render: IconBlockedNotification,
  type: NotificationBarType.ERROR,
  removeType: NotificationRemoveType.NEVER,
  isWithoutReadMore: true,
  ts_created: 1,
  shouldBeShown: getShouldShowIconBlockedNotification,
}

export const AllNotifications: StaticNotification[] = [
  ActivateProTrialNotificationConfig,
  AfterExpiredPeriodNotificationConfig,
  BlockedAccountNotificationConfig,
  BlockedSMSChannelNotificationConfig,
  ExpiredNotificationConfig,
  IGChangeAccountTypeErrorNotificationConfig,
  PayNowNotificationConfig,
  PaymentCardRenewalNotificationConfig,
  RefreshAdminPermissionsNotificationConfig,
  VerifySubscriptionPaymentNotificationConfig,
  WAFreePeriodEndNotificationConfig,
  WalletLowBalanceNotificationConfig,
  WhatsAppBlockedNotificationConfig,
  IgRefreshPermissionsNotificationConfig,
  WaHistoryContactImportStartedNotificationConfig,
  WaHistoryContactImportFinishedNotificationConfig,
  TikTokChannelUsRegionRestrictionNotificationConfig,
  WaCreditLineIssueNotificationConfig,
  WaCoexistenceContactImportAvailableNotificationConfig,
  WaPricingUpdateNotificationConfig,
  WaServiceMessagesChargeNotificationConfig,
  PlanDowngradeNotificationConfig,
  BlockedByValueMetricNotificationConfig,
  MigrateFreeUsersToNewBillingNotificationConfig,
  Discount90DaysNotificationConfig,
  PostMigrationFreeUsersToNewBillingNotificationConfig,
  ExploreModeConnectInstagramNotificationConfig,
  ForcedNpmMigrationNotificationConfig,
  AnnualExpansionNotificationConfig,
  IconBlockedNotificationConfig,
]
